import {
  EVENT_ID,
  type ApiConfig,
  type Session,
  type SyncConflict,
  type SyncMutation,
  type Ticket,
} from "./types";
import {
  emptyCashRecord,
  normalizeCashRecord,
  type CashCheckpoint,
  type CashCountRecord,
} from "./cash";

const KEYS = {
  config: "ii-field-config-v1",
  tickets: "ii-field-tickets-v1",
  lastSync: "ii-field-last-sync-v1",
  queue: "ii-field-queue-v1",
  conflicts: "ii-field-conflicts-v1",
  device: "ii-field-device-v1",
  performerOptions: "ii-field-performers-v1",
  cashCounts: "ii-field-cash-v1",
};

type PerformerOptions = Record<Session, string[]>;

const EMPTY_PERFORMER_OPTIONS: PerformerOptions = { afternoon: [], evening: [] };
const CASH_CHECKPOINTS: CashCheckpoint[] = ["opening", "afternoon", "evening"];

function readJson<T>(key: string, fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
}

function defaultCashCounts(): Record<CashCheckpoint, CashCountRecord> {
  return {
    opening: emptyCashRecord("opening"),
    afternoon: emptyCashRecord("afternoon"),
    evening: emptyCashRecord("evening"),
  };
}

export const storage = {
  getConfig: (): ApiConfig => readJson(KEYS.config, { endpoint: "", token: "" }),
  setConfig: (value: ApiConfig) => localStorage.setItem(KEYS.config, JSON.stringify(value)),
  getTickets: (): Ticket[] => readJson(KEYS.tickets, []),
  setTickets: (value: Ticket[]) => localStorage.setItem(KEYS.tickets, JSON.stringify(value)),
  getPerformerOptions: (): PerformerOptions => {
    const value = readJson<Partial<PerformerOptions>>(KEYS.performerOptions, EMPTY_PERFORMER_OPTIONS);
    return {
      afternoon: Array.isArray(value.afternoon) ? value.afternoon : [],
      evening: Array.isArray(value.evening) ? value.evening : [],
    };
  },
  setPerformerOptions: (value: PerformerOptions) =>
    localStorage.setItem(KEYS.performerOptions, JSON.stringify(value)),
  getCashCounts: (): Record<CashCheckpoint, CashCountRecord> => {
    const raw = readJson<Partial<Record<CashCheckpoint, Partial<CashCountRecord>>>>(
      KEYS.cashCounts,
      {},
    );
    const next = defaultCashCounts();
    for (const checkpoint of CASH_CHECKPOINTS) {
      if (raw[checkpoint]) {
        next[checkpoint] = normalizeCashRecord({
          ...raw[checkpoint],
          checkpoint,
        });
      }
    }
    return next;
  },
  setCashCounts: (value: Record<CashCheckpoint, CashCountRecord>) =>
    localStorage.setItem(KEYS.cashCounts, JSON.stringify(value)),
  getLastSync: (): string => localStorage.getItem(KEYS.lastSync) || "",
  setLastSync: (value: string) => localStorage.setItem(KEYS.lastSync, value),
  clearLastSync: () => localStorage.removeItem(KEYS.lastSync),
  getQueue: (): SyncMutation[] =>
    readJson<Array<Partial<SyncMutation>>>(KEYS.queue, [])
      .filter((mutation): mutation is SyncMutation => Boolean(mutation.unitId && mutation.kind))
      .map((mutation) => ({ ...mutation, eventId: mutation.eventId || EVENT_ID })),
  setQueue: (value: SyncMutation[]) => localStorage.setItem(KEYS.queue, JSON.stringify(value)),
  getConflicts: (): SyncConflict[] => readJson(KEYS.conflicts, []),
  setConflicts: (value: SyncConflict[]) =>
    localStorage.setItem(KEYS.conflicts, JSON.stringify(value)),
  getDeviceId: (): string => {
    const existing = localStorage.getItem(KEYS.device);
    if (existing) return existing;
    let suffix = "";
    try {
      suffix = crypto.randomUUID().slice(0, 8);
    } catch {
      suffix = `${Date.now().toString(36)}${Math.random().toString(16).slice(2, 6)}`;
    }
    const value = `field-${suffix}`;
    localStorage.setItem(KEYS.device, value);
    return value;
  },
  getDeviceRole: (): string => localStorage.getItem("ii-field-device-role-v1") || "general",
  setDeviceRole: (value: string) => localStorage.setItem("ii-field-device-role-v1", value),
  getFieldToken: (): string => localStorage.getItem("ii-field-token-v1") || "",
  setFieldToken: (value: string) => localStorage.setItem("ii-field-token-v1", value),
  isFieldBackend: (endpoint: string): boolean =>
    /\/api\/field\/?$/.test(endpoint) || endpoint.includes("/functions/v1/field-sync"),
};
